import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../utils/cn"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer",
  {
    variants: {
      variant: {
        ghost: `bg-transparent text-tw-text
          hover:bg-tw-surface-bg-hvy-1 focus:bg-tw-surface-bg-hvy-1
          active:bg-tw-surface-bg-hvy-7 active:text-tw-text-invert
          disabled:border-tw-border-lite disabled:text-tw-text-lite-4 disabled:cursor-not-allowed disabled:pointer-events-none
          data-[disabled=true]:border-tw-border-lite data-[disabled=true]:text-tw-text-lite-4 data-[disabled=true]:cursor-not-allowed data-[disabled=true]:pointer-events-none`,
        action: `bg-transparent text-tw-action-fg font-semibold
         hover:text-tw-action-fg-hvy-2
         focus:text-tw-action-fg-hvy-2
         active:text-tw-action-fg-hvy-4`,
        primary: `bg-tw-surface-bg-hvy-7 text-tw-text-invert
           hover:bg-tw-action-fg-hvy-4
           focus:bg-tw-action-fg-hvy-4
           active:bg-tw-action-fg-hvy-3
           disabled:bg-tw-surface-bg-hvy-1 disabled:text-tw-text-lite-3
           data-[disabled=true]:bg-tw-surface-bg-hvy-1 data-[disabled=true]:text-tw-text-lite-3`,
        secondary: `bg-tw-surface-bg-hvy-2 text-tw-text
           hover:bg-tw-surface-bg-hvy-7 hover:text-tw-text-invert
           focus:bg-tw-surface-bg-hvy-7 focus:text-tw-text-invert
           active:bg-tw-text-lite-2 active:text-tw-text-invert
           disabled:bg-tw-surface-bg-hvy-1 disabled:text-tw-text-lite-3
           data-[disabled=true]:bg-tw-surface-bg-hvy-1 data-[disabled=true]:text-tw-text-lite-3 `,
        outline: `bg-transparent text-tw-text ring-1 ring-tw-border
           hover:bg-tw-surface-bg-hvy-1 focus:bg-tw-surface-bg-hvy-1
           active:bg-tw-surface-bg-hvy-7 active:text-tw-text-invert
           disabled:border-tw-border-lite disabled:text-tw-text-lite-4 disabled:cursor-not-allowed disabled:pointer-events-none
           data-[disabled=true]:border-tw-border-lite data-[disabled=true]:text-tw-text-lite-4 data-[disabled=true]:cursor-not-allowed data-[disabled=true]:pointer-events-none`,
        //  TODO: we should add colors to the props, so we can use variance + colors
        "outline-danger": `bg-transparent text-tw-danger-fg ring-1 ring-tw-danger-fg
            hover:bg-tw-danger-bg-lite focus:bg-tw-danger-bg-lite`,
      },
      size: {
        default: "h-10 px-4 py-2",
        xs: "h-7 rounded-md px-2 text-xs",
        sm: "h-8 rounded-md px-3",
        lg: "h-12 rounded-md px-8 text-base",
        icon: "h-10 w-10",
        "icon-sm": "h-8 w-8",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
    compoundVariants: [
      {
        variant: "action",
        size: "sm",
        className: "-my-1 -mx-3",
      },
    ],
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
